// Screen Node Component for Flow Builder

import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  Box,
  Typography,
  Chip
} from '@mui/material';

interface ScreenNodeProps {
  data: {
    label: string;
    description?: string;
    screenType?: string;
    components?: Array<{
      type: string;
      label: string;
      required?: boolean;
    }>;
    status?: string;
    color?: string;
  };
  selected?: boolean;
}

const ScreenNode: React.FC<ScreenNodeProps> = ({ data, selected }) => {
  const { 
    label = 'Screen', 
    description = 'Interactive user interface',
    screenType = 'Form',
    components = [],
    status = 'idle',
    color = '#96CEB4'
  } = data;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#4CAF50';
      case 'failed': return '#F44336';
      case 'running': return '#FF9800';
      default: return '#9E9E9E';
    }
  };

  return (
    <Box
      sx={{
        minWidth: 220,
        maxWidth: 320,
        background: 'linear-gradient(135deg, rgba(150, 206, 180, 0.1) 0%, rgba(150, 206, 180, 0.2) 100%)',
        border: selected ? `3px solid ${color}` : `2px solid ${color}`,
        borderRadius: '16px',
        padding: 2,
        boxShadow: selected 
          ? `0 8px 25px rgba(150, 206, 180, 0.4)` 
          : '0 4px 12px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(150, 206, 180, 0.3)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      {/* Top decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, rgba(150, 206, 180, 0.6) 100%)`
        }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '12px',
            backgroundColor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.4rem',
            color: 'white'
          }}
        >
          📱
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', lineHeight: 1 }}>
            {screenType} Screen
          </Typography>
        </Box>
        <Chip
          size="small"
          label={status}
          sx={{
            backgroundColor: getStatusColor(status),
            color: 'white',
            fontSize: '0.7rem',
            height: 20
          }}
        />
      </Box>

      {/* Description */}
      <Typography 
        variant="body2" 
        sx={{ 
          color: '#555', 
          mb: 1.5, 
          fontSize: '0.8rem',
          lineHeight: 1.3
        }}
      >
        {description}
      </Typography>

      {/* Screen Preview */}
      <Box
        sx={{
          background: 'rgba(255,255,255,0.8)',
          border: '1px solid rgba(150, 206, 180, 0.3)',
          borderRadius: '8px',
          p: 1.5,
          mb: 1
        }}
      >
        <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold', mb: 1, display: 'block' }}>
          Components ({components.length}):
        </Typography>
        
        {components.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {components.slice(0, 3).map((component, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '4px',
                    backgroundColor: component.required ? '#FF9800' : '#4CAF50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography sx={{ fontSize: '0.6rem', color: 'white' }}>
                    {component.type.charAt(0).toUpperCase()}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#333', flex: 1 }}>
                  {component.label}
                </Typography>
                {component.required && (
                  <Typography variant="caption" sx={{ color: '#FF9800', fontSize: '0.6rem' }}>
                    *
                  </Typography>
                )}
              </Box>
            ))}
            {components.length > 3 && (
              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                +{components.length - 3} more components...
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
            No components configured
          </Typography>
        )}
      </Box>

      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: color,
          border: '2px solid white',
          width: 12,
          height: 12
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: color,
          border: '2px solid white',
          width: 12,
          height: 12
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="next"
        style={{
          background: '#4CAF50',
          border: '2px solid white',
          width: 10,
          height: 10,
          top: '60%'
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="cancel"
        style={{
          background: '#F44336',
          border: '2px solid white',
          width: 10,
          height: 10,
          top: '80%'
        }}
      />
    </Box>
  );
};

export default ScreenNode;